import { DependencyList, useEffect } from 'react';
import { castDraft } from 'immer';
import useImmutableState from './useImmutableState';
import useMounted from './useMounted';

export interface IFetching<Data> {
    data: Data;
    fetching: boolean;
    isError: boolean;
}

const useFetching = <Data>(
    callback: () => Promise<Data>,
    deps: DependencyList,
    mutation?: (data: Data) => Data,
): IFetching<Data> => {
    const [state, updateState] = useImmutableState<IFetching<Data>>({
        fetching: true,
        isError: false,
        data: null,
    });

    const mounted = useMounted();

    useEffect(() => {
        updateState((draft) => {
            draft.fetching = true;
        });

        callback()
            .then((data: Data) => {
                if (mounted.current) {
                    updateState((draft) => {
                        draft.data = castDraft(mutation ? mutation(data) : data);
                        draft.fetching = false;
                    });
                }
            })
            .catch(() => {
                updateState((draft) => {
                    draft.isError = true;
                    draft.fetching = false;
                });
            });
    }, deps);

    return state;
};

export default useFetching;
